import store from '@/store';

import api from '@/services/api';
import identite from '@/services/api/compte/identite';
import session from '@/services/api/compte/session';

import { PageMapping } from '@/helpers/PageMappingHelpers';
import { returnToHome } from '@/helpers/RouteHelpers';
import { formatAddressDetailsToAddressFormData } from '@/helpers/AddressHelpers';

import {
  isProductSource2eChance,
  getProductSourceFromQueryParams,
} from '@/helpers/ProductSourceHelpers';

// Constantes pour l'API AddressFind
const ADDRESS_SEARCH_LIMIT = 200;
const DEFAULT_LANGUAGE = 'fr';
const DEFAULT_COUNTRY = 'CA';
const DEFAULT_PROVINCE = 'QC';

function persistRetrievedAddress(retrievedAddress) {
  if (!retrievedAddress) {
    return;
  }

  const formattedAddress = formatAddressDetailsToAddressFormData(retrievedAddress);
  if (formattedAddress) {
    try {
      localStorage.setItem('retrieved_address', JSON.stringify(formattedAddress));
    } catch (storageError) {
      // Erreur silencieuse : le localStorage peut être désactivé.
    }
  }
}

// AddressFindRequest du jel-module attend la locale applicative ('fr' / 'en').
// AddressServiceImpl la convertit ensuite au format requis par Loqate.
function normalizeAddressLanguage(language) {
  if (!language) return DEFAULT_LANGUAGE;
  const lower = String(language).toLowerCase();
  if (lower === 'fr' || lower.startsWith('fr-') || lower.startsWith('fr_')) {
    return 'fr';
  }
  if (lower === 'en' || lower.startsWith('en-') || lower.startsWith('en_')) {
    return 'en';
  }
  return DEFAULT_LANGUAGE;
}

function getAddressItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.Items)) {
    return responseData.Items;
  }
  return responseData;
}

export default {
  /**
   * Vérifie si un résultat de recherche est une adresse
   *
   * @param {Object} result - Résultat de la recherche
   * @returns {boolean} true si c'est une adresse
   */
  isAddress(result) {
    return result && result.Type === 'Address';
  },

  /**
   * Vérifie si un résultat de recherche est un container
   *
   * @param {Object} result - Résultat de la recherche
   * @returns {boolean} true si c'est un container
   */
  isContainer(result) {
    return result && result.Type === 'Container';
  },

  /**
   * Extrait le nombre d'éléments d'un container depuis la Description
   *
   * @param {Object} containerResult - Résultat de type Container
   * @returns {string} Nombre d'éléments formaté (ex: "5 adresses")
   */
  getContainerCount(containerResult) {
    if (!this.isContainer(containerResult)) return '';

    // La Description contient le nombre d'éléments
    // Format typique: "5" ou "5 items"
    const description = containerResult.Description || '';

    // Extraire le nombre
    const match = description.match(/\d+/);
    if (match) {
      const count = parseInt(match[0], 10);
      return count > 1 ? `${count} adresses` : `${count} adresse`;
    }

    return description;
  },

  /**
   * Recherche d'adresses via AddressFind API
   *
   * Cas 1: Recherche par valeur saisie par le client
   * - Appelé à chaque caractère saisi (sauf caractères spéciaux)
   * - Retourne une liste d'adresses ou de containers
   *
   * Cas 2: Sélection d'un container
   * - Appelé avec l'ID du container pour obtenir les adresses enfants
   *
   * @param {string} text - Texte de recherche saisi par l'utilisateur
   * @param {string|null} containerId - ID du container (null pour recherche libre)
   * @param {string} language - Langue du fureteur ('fr' ou 'en')
   * @returns {Promise<Array>} Liste des résultats de recherche
   *
   * Structure de réponse attendue:
   * [
   *   {
   *     Id: string,           // Identifiant unique
   *     Type: string,         // "Address" ou "Container"
   *     Text: string,         // Texte à afficher dans la liste
   *     Description: string,  // Pour Container: nombre d'éléments
   *     Highlight: string     // Partie surlignée du texte
   *   }
   * ]
   */
  async rechercherAdresse(text, containerId = null, language = DEFAULT_LANGUAGE) {
    try {
      // Nettoyer le texte: retirer les caractères spéciaux (sauf accents et tirets)
      const cleanedText = text.replace(/[^\w\s\u00C0-\u017F-]/g, '').trim();

      // Ne pas appeler si le texte est vide
      if (!cleanedText) {
        return [];
      }

      const data = {
        text: cleanedText,
        language: normalizeAddressLanguage(language),
        limit: ADDRESS_SEARCH_LIMIT,
      };

      // Si un containerId est fourni, l'ajouter aux paramètres
      if (containerId) {
        data.container = containerId;
      }

      // Appel au backend jel-module: AddressController.find()
      // product_source est transmis pour permettre l'accès sans session au flux 2eChance
      const response = await api.call('address/find', 'addressFind', 'POST', {
        data,
        params: { product_source: getProductSourceFromQueryParams() },
        withCredentials: true,
        ignoreMgnlMock: true,
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      });

      // Le backend peut retourner directement le tableau ou la reponse AddressFind
      // brute sous { Items }.
      if (response.data?.status === 'ERROR') {
        const serviceError = new Error(
          response.data?.msg || "Service de recherche d'adresse indisponible"
        );
        serviceError.response = response;
        throw serviceError;
      }

      const results = getAddressItems(response.data);
      return Array.isArray(results) ? results : [];
    } catch (error) {
      console.error("Erreur lors de la recherche d'adresses:", error);

      // Laisser le composant afficher l'indisponibilité de AddressFind.
      throw error;
    }
  },

  /**
   * Récupération des détails d'une adresse
   * via le service de Récupération d'adresse
   *
   * Sélection d'une adresse (Type="Address")
   * - Appelé avec l'ID de l'adresse sélectionnée
   * - Retourne les détails complets de l'adresse
   *
   * @param {string} addressId - ID de l'adresse à récupérer
   * @returns {Promise<Object>} Détails de l'adresse
   *
   * Structure de réponse attendue:
   * {
   *   Id: string,
   *   Type: string,
   *   Text: string,
   *   Description: string,
   *   Language: string,
   *   LanguageAlternatives: string,
   *   SubBuilding: string,        // Numéro d'appartement
   *   BuildingNumber: string,     // Numéro civique
   *   Street: string,             // Nom de la rue
   *   City: string,               // Ville
   *   Province: string,           // Province (QC)
   *   PostalCode: string,         // Code postal
   *   CountryName: string,
   *   CountryIso2: string,
   *   CountryIso3: string
   * }
   */
  async recupererAdresse(addressId) {
    try {
      if (!addressId) {
        throw new Error("L'ID de l'adresse est requis");
      }

      const data = {
        id: addressId,
      };

      // Appel au backend jel-module: AddressController.retrieve()
      // product_source est transmis pour permettre l'accès sans session au flux 2eChance
      const response = await api.call('address/retrieve', 'addressRetrieve', 'POST', {
        data,
        params: { product_source: getProductSourceFromQueryParams() },
        withCredentials: true,
        ignoreMgnlMock: true,
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      });

      // La réponse de Address Retrieve retourne un tableau avec un seul élément
      // On extrait le premier élément du tableau
      const addressDetails = getAddressItems(response.data);

      let retrievedAddress = null;
      if (Array.isArray(addressDetails) && addressDetails.length > 0) {
        [retrievedAddress] = addressDetails;
      } else {
        // Si ce n'est pas un tableau, retourner tel quel (compatibilité)
        retrievedAddress = addressDetails || null;
      }

      // Sauvegarder l'adresse formatée dans le localStorage
      persistRetrievedAddress(retrievedAddress);

      return retrievedAddress;
    } catch (error) {
      console.error("Erreur lors de la récupération des détails de l'adresse:", error);
      throw error;
    }
  },

  /**
   * Vérifie une adresse via le service Address Verify
   * Utilisé pour valider l'adresse lorsque le client confirme son adresse
   *
   * @returns {Promise<Object>} Résultat de la vérification d'adresse
   * Structure de réponse:
   * {
   *   validated: boolean,
   *   exactMatch: boolean,
   *   validationStatus: 'SUCCESS' | 'PARTIAL_MATCH' | 'NOT_FOUND' | 'ERROR',
   *   suggestedAddress: { ... } | null,
   *   aqi: string,                   // Indicateur de qualité d'adresse
   *   avc: string                    // Code de vérification d'adresse
   * }
   */
  async verifierAdresse() {
    const state = store.state.compte.adresse;

    try {
      // Validation des champs requis
      if (!state.ville || !state.codePostal) {
        throw new Error('Ville et code postal sont requis');
      }

      // Construire l'adresse au format attendu par Address Verify
      // Format: "numéro rue, appartement, ville province codePostal"

      // Ligne 1: numéro + rue (+ appartement si présent)
      let address1 = '';
      if (state.numero) {
        address1 += state.numero;
      }
      if (state.rue) {
        address1 += (address1 ? ' ' : '') + state.rue;
      }

      // Préparer les données au format attendu par l'API Address Verify
      // Le service attend un tableau "addresses" avec une structure spécifique
      const data = {
        Addresses: [
          {
            Address: '', // Adresse complète (optionnel si Address1 fourni)
            Address1: address1, // Ligne 1 de l'adresse
            Address2: state.appartement || '', // Ligne 2 de l'adresse
            AdministrativeArea: state.province || DEFAULT_PROVINCE, // Province
            Locality: state.ville || '',
            PostalCode: state.codePostal || '', // Code postal
            Country: state.pays || DEFAULT_COUNTRY, // Pays
          },
        ],
      };

      // Appel au backend jel-module: AddressController.verify()
      // product_source est transmis pour permettre l'accès sans session au flux 2eChance
      const response = await api.call('address/verify', 'addressVerify', 'POST', {
        data,
        params: { product_source: getProductSourceFromQueryParams() },
        withCredentials: true,
        ignoreMgnlMock: true,
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      });

      // Récupérer le résultat de la vérification
      const result = response.data || null;

      // Sauvegarder le résultat de la validation dans le store
      store.commit('compte/SET_ADRESSE_VERIFICATION_RESULT', result);

      return result;
    } catch (error) {
      console.error(`Erreur lors de la vérification d'adresse: ${error}`);

      // En cas d'erreur, retourner un objet avec le statut ERROR
      return {
        validated: false,
        exactMatch: false,
        validationStatus: 'ERROR',
        suggestedAddress: null,
        error: error.message || "Erreur lors de la vérification de l'adresse",
      };
    }
  },

  // Tâche Magnolia pour:
  // identite-documents-adresse
  async sauvegarderAdresse() {
    if (session.sessionEstOuverte()) {
      return _sauvegarderAdresse();
    }
  },

  // Tâche Magnolia pour:
  // adresse-2e-chance
  async validerAdresse() {
    if (process.env.VUE_APP_NEW_REG_FLOW === 'true') {
      if (!session.sessionEstOuverte()) {
        session.cleanUpSession();
        returnToHome();
      }
      return _sauvegarderAdresse();
    }
  },

  // Tâche Magnolia pour:
  // identite-adresse
  // identite-adresse-suggestion
  async sauvegarderAdresseEtValiderIdentite() {
    await _sauvegarderAdresse()
      .then(async () => {
        await _validerIdentite()
          .then(() => {
            store.commit('compte/RESET');
            store.commit('profile/RESET');
            throw { url: PageMapping.IDENTITE_CONFIRMATION.siteUrl };
          })
          .catch((error) => {
            if (error.url) {
              throw error;
            }
            throw { url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl };
          });
      })
      .catch((error) => {
        if (error.url) {
          throw error;
        }
        throw { url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl };
      });
  },
};

async function _sauvegarderAdresse() {
  const adresseState =
    store.state.compte.choixAdresseValidation && store.state.compte.choixAdresseValidation.rue
      ? store.state.compte.choixAdresseValidation
      : store.state.compte.adresse;
  const rue = adresseState.rue.replace(/['']/gi, "'");
  const ville = adresseState.ville.replace(/['']/gi, "'");

  // Mettre a jour l'adresse du compte avec celle choisie qui sera utilisée pour la validation d'identité
  store.commit('compte/UPDATE_ADDRESS', adresseState);

  // Construire addressLine3 en combinant appartement et complément d'adresse
  let addressLine2 = '';
  if (adresseState.rue) {
    addressLine2 = rue;
  }
  if (adresseState.complementAdresse) {
    addressLine2 = addressLine2
      ? `${addressLine2} ${adresseState.complementAdresse}`
      : adresseState.complementAdresse;
  }

  return new Promise((resolve, reject) => {
    const data = {
      addressLine1: adresseState.numero,
      addressLine2,
      addressLine3: adresseState.appartement,
      city: ville,
      postcode: adresseState.codePostal,
      countryCode: 'CAN',
      state: 'QC',
      custUUID: sessionStorage.getItem('lq_client_uuid'),
      rejectLoqate: store.state.compte.indRefusPropositionAdresse === true,
      progressiveMigration: process.env.VUE_APP_PROG_MIGRATION === 'true',
      dossier: {
        clientMigrated: store.state.dossier.clientMigrated,
        versionDonnees: store.state.dossier.versionDonnees,
      },
    };
    api
      .call('players/address', 'playersAddressPost', 'POST', {
        data,
        params: { withValidation: false },
      })
      .then((response) => {
        // Migrated client path
        if (Object.keys(response.data?.playerProfile || {}).length > 0) {
          // Nouvelle version données après sauvegarde de l'adresse
          const versionDonnees = response.data.playerProfile?.versionDonnees;
          store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', versionDonnees);
          if (response.data.playerProfile.isDuplicate) {
            const url = isProductSource2eChance()
              ? PageMapping.COMPTE_DOUBLON_2E_CHANCE.siteUrl
              : PageMapping.IDENTITE_ECHEC_DOUBLON.siteUrl;
            session.fermerSession().then(() => {
              reject({ url });
            });
          } else {
            _validationSuccess(response, resolve, reject);
          }
        }
        // Default path
        else {
          _validationSuccess(response, resolve, reject);
        }
      })
      .catch((error) => {
        console.error('sauvegarderAdresse | failed: ', error);
        reject(error);
      });
  });
}

async function _validationSuccess(response, resolve, reject) {
  if (response.data && response.data.status === 'ERROR') {
    if (response.data.msg === 'CONNEXION') {
      session.fermerSession();
      returnToHome();
    } else {
      reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
    }
  } else if (response.data && response.data.status === 'SUCCESS') {
    /**
     * Spécifications: https://lotoquebec.atlassian.net/browse/SWSPC-1649
     *
     * On ne reset pas les données pour éviter une erreur générique
     * si l'utilisateur fait un retour par le Back du fureteur
     * dans la page de validation de l'adresse
     *
     * */
    resolve(response.data);
  }
}

async function _validerIdentite() {
  return new Promise((resolve, reject) => {
    identite
      .identityValidation()
      .then(async (response) => {
        console.log('identityValidation | response: ', response);
        if (response.data && response.data.status === 'ERROR') {
          if (response.data.msg === 'CONNEXION') {
            session.fermerSession();
            returnToHome();
          } else if (response.data.msg === 'MSG01') {
            reject({ url: PageMapping.ERREUR_GENERIQUE.siteUrl });
          } else {
            reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
          }
        } else if (response.data && response.data.PLAYER_STATUS === 'PLAYER') {
          reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
        } else if (response.data && response.data.status === 'SUCCESS') {
          switch (response.data.validationInfo.validationStatus) {
            case 'OB_OK':
              // Happy Path
              resolve();
              break;
            case 'IDENTITYMANUAL':
              reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
              break;
            case 'IDENTITYFAIL':
            case 'PENDINGID':
            case 'WATCHLIST':
              console.error('identityValidation | failed: ', response.data.validationInfo);
              reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
              break;
            case 'URNFAILURE':
              session.fermerSession().then(() => {
                reject({ url: PageMapping.IDENTITE_ECHEC_DOUBLON.siteUrl });
              });
              break;
            case 'CARDEXFAILURE':
              session.fermerSession().then(() => {
                reject({
                  url: PageMapping.IDENTITE_ECHEC_AUTOEXCLUSION.siteUrl,
                });
              });
              break;
            case 'ERROREXTERNALSERVICE':
              console.error('identityValidation | failed : ', response.data.validationInfo);
              reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
              break;
            case 'PAYSAFE_PROFILE_CREATION_FAILED':
              console.error(
                'identityValidation | Paysafe Profile Creation failed : ',
                response.data.messages
              );
              reject({ url: PageMapping.IDENTITE_ECHEC.siteUrl });
              break;
            default:
              reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
              break;
          }
        } else {
          reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
        }
      })
      .catch((error) => {
        console.error('identityValidation | fail: ', error);
        if (error.url) {
          reject({ url: error.url });
        }
        reject({ url: PageMapping.ERREUR_IDENTITE_ADRESSE.siteUrl });
      });
  });
}
